// Vista de detalle de un trade. Tabs: Resumen / Pre-análisis / Post-análisis.
// Cada tab muestra journal status (completo/pendiente) — el usuario sabe si
// le falta registrar algo.
//
// Cambios estructurales (F3.4):
//   - 3 tabs separan la info por etapa (resumen, pre, post)
//   - Header muestra estado del journal (Pre ✓/⚠ · Post ✓/⚠)
//   - Las screenshots se filtran por phase en cada tab
//
// La edición sigue siendo via TradeFormModal (botón Editar).

import { useState, type ReactElement } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { Trade, TradingAccount } from '@bini-trading/shared-types';
import { api } from '../../lib/api';
import { cn } from '../../lib/cn';
import { Button } from '../ui/Button';
import { Modal } from '../ui/Modal';
import { TradeFormModal } from './TradeFormModal';
import { LinkTradeModal } from './LinkTradeModal';

const fmt = (n: number, opts: Intl.NumberFormatOptions = {}): string =>
  new Intl.NumberFormat('es-CL', { maximumFractionDigits: 5, ...opts }).format(n);
const fmtDateTime = (iso: string): string =>
  new Date(iso).toLocaleString('es-CL', {
    day: '2-digit',
    month: 'short',
    year: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });

interface Props {
  trade: Trade | null;
  accounts: TradingAccount[];
  allTrades?: Trade[];
  onClose: () => void;
}

const OUTCOME_LABEL: Record<string, { label: string; color: string }> = {
  tp: { label: '✓ TP', color: 'bg-win-DEFAULT/15 border-win-DEFAULT/40 text-win-DEFAULT' },
  sl: { label: '✕ SL', color: 'bg-loss-DEFAULT/15 border-loss-DEFAULT/40 text-loss-DEFAULT' },
  manual: { label: '✋ Manual', color: 'bg-info-DEFAULT/15 border-info-DEFAULT/40 text-info-DEFAULT' },
  breakeven: { label: '= BE', color: 'bg-surface-strong border-ink-muted text-ink' },
  trailing: { label: '↗ Trail', color: 'bg-warn-DEFAULT/15 border-warn-DEFAULT/40 text-warn-DEFAULT' },
};

type TabId = 'summary' | 'pre' | 'post';

export function TradeDetailModal({
  trade,
  accounts,
  allTrades = [],
  onClose,
}: Props): ReactElement | null {
  const qc = useQueryClient();
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [editing, setEditing] = useState(false);
  const [linking, setLinking] = useState(false);
  const [zoomImg, setZoomImg] = useState<string | null>(null);
  const [tab, setTab] = useState<TabId>('summary');

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api(`/trades/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['trades'] });
      qc.invalidateQueries({ queryKey: ['pending-summary'] });
      qc.invalidateQueries({ queryKey: ['analytics-calendar'] });
      onClose();
      setConfirmDelete(false);
    },
  });

  if (!trade) return null;

  const isLong = trade.side === 'long';
  const isOpen = trade.status === 'open';
  const sideColor = isLong ? 'text-win-DEFAULT' : 'text-loss-DEFAULT';
  const pnl = trade.pnl ?? 0;
  const pnlColor =
    pnl > 0 ? 'text-win-DEFAULT' : pnl < 0 ? 'text-loss-DEFAULT' : 'text-ink-muted';

  const preShots = trade.screenshots.filter((s) => s.phase === 'pre' || s.phase === 'entry');
  const postShots = trade.screenshots.filter((s) => s.phase === 'post');
  const otherShots = trade.screenshots.filter((s) => !s.phase);

  const journal = trade.journal;
  const preComplete = journal?.preComplete ?? false;
  const postComplete = journal?.postComplete ?? false;

  return (
    <>
      <Modal
        open={!!trade && !editing}
        onClose={onClose}
        title={
          <span className="flex items-center gap-2 flex-wrap">
            <span>{trade.symbol}</span>
            <span className="text-ink-muted">·</span>
            <span>{isLong ? 'Long' : 'Short'}</span>
            {isOpen && (
              <span className="text-[10px] font-bold uppercase tracking-wider text-info-DEFAULT bg-info-DEFAULT/10 border border-info-DEFAULT/30 px-2 py-0.5 rounded flex items-center gap-1">
                <span className="w-1.5 h-1.5 rounded-full bg-info-DEFAULT animate-pulse" />
                Live
              </span>
            )}
          </span>
        }
        description={`Trade del ${fmtDateTime(trade.entryAt)}${trade.source === 'mt5' ? ' · importado de MT5' : ''}`}
        size="lg"
        footer={
          <div className="flex items-center justify-between gap-2 w-full flex-wrap">
            <Button variant="danger" size="sm" onClick={() => setConfirmDelete(true)}>
              ✕ Eliminar
            </Button>
            <div className="flex gap-2 flex-wrap">
              <Button variant="ghost" size="sm" onClick={() => setLinking(true)}>
                🔗 Linkear
              </Button>
              <Button variant="ghost" onClick={() => setEditing(true)}>
                ✎ Editar
              </Button>
              <Button variant="secondary" onClick={onClose}>
                Cerrar
              </Button>
            </div>
          </div>
        }
      >
        <div className="space-y-4">
          {/* Tabs */}
          <div className="flex border-b border-surface-border -mt-1">
            <TabButton
              active={tab === 'summary'}
              onClick={() => setTab('summary')}
              label="Resumen"
            />
            <TabButton
              active={tab === 'pre'}
              onClick={() => setTab('pre')}
              label="Pre-análisis"
              status={preComplete ? 'ok' : 'pending'}
            />
            <TabButton
              active={tab === 'post'}
              onClick={() => setTab('post')}
              label="Post-análisis"
              status={isOpen ? 'na' : postComplete ? 'ok' : 'pending'}
            />
          </div>

          {tab === 'summary' && (
            <SummaryTab
              trade={trade}
              isLong={isLong}
              isOpen={isOpen}
              sideColor={sideColor}
              pnlColor={pnlColor}
              otherShots={otherShots}
              onZoom={setZoomImg}
              onGoTab={setTab}
            />
          )}

          {tab === 'pre' && (
            <PrePostTab
              kind="pre"
              text={trade.preAnalysis}
              shots={preShots}
              complete={preComplete}
              onEdit={() => setEditing(true)}
              onZoom={setZoomImg}
            />
          )}

          {tab === 'post' && (
            <PrePostTab
              kind="post"
              text={trade.postAnalysis}
              shots={postShots}
              complete={postComplete}
              outcome={trade.outcome}
              isOpen={isOpen}
              onEdit={() => setEditing(true)}
              onZoom={setZoomImg}
            />
          )}
        </div>
      </Modal>

      {/* Confirmar borrado */}
      <Modal
        open={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        title="Eliminar trade"
        size="sm"
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setConfirmDelete(false)}>
              Cancelar
            </Button>
            <Button
              variant="primary"
              loading={deleteMutation.isPending}
              onClick={() => deleteMutation.mutate(trade.id)}
            >
              Sí, eliminar
            </Button>
          </div>
        }
      >
        <p className="text-sm text-ink-muted">
          Vas a eliminar este trade y todos sus screenshots. No se puede deshacer.
        </p>
      </Modal>

      {/* Modal de edición — pre-llenado con el trade */}
      {editing && (
        <TradeFormModal
          open={editing}
          onClose={() => {
            setEditing(false);
            // Cerrar también el detail después de guardar para refrescar con
            // los nuevos datos vía useQuery cuando el user reabra.
            onClose();
          }}
          accounts={accounts}
          trade={trade}
        />
      )}

      {/* Modal para fusionar con otro trade */}
      {linking && (
        <LinkTradeModal
          open={linking}
          currentTrade={trade}
          allTrades={allTrades}
          onClose={() => setLinking(false)}
          onMerged={() => {
            setLinking(false);
            onClose();
          }}
        />
      )}

      {/* Lightbox simple para zoom */}
      {zoomImg && (
        <div
          onClick={() => setZoomImg(null)}
          className="fixed inset-0 z-[60] bg-black/90 flex items-center justify-center p-6 cursor-zoom-out"
        >
          <img
            src={zoomImg}
            alt=""
            className="max-w-full max-h-full object-contain"
          />
          <button
            onClick={() => setZoomImg(null)}
            className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 text-white text-xl flex items-center justify-center"
          >
            ×
          </button>
        </div>
      )}
    </>
  );
}

function TabButton({
  active,
  onClick,
  label,
  status,
}: {
  active: boolean;
  onClick: () => void;
  label: string;
  status?: 'ok' | 'pending' | 'na';
}): ReactElement {
  const indicator =
    status === 'ok' ? '✓' : status === 'pending' ? '⚠' : status === 'na' ? '—' : null;
  const indicatorColor =
    status === 'ok'
      ? 'text-win-DEFAULT'
      : status === 'pending'
        ? 'text-warn-DEFAULT'
        : 'text-ink-faint';
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors flex items-center gap-1.5',
        active
          ? 'border-brand-500 text-ink'
          : 'border-transparent text-ink-muted hover:text-ink',
      )}
    >
      {label}
      {indicator && (
        <span className={cn('text-xs font-bold', indicatorColor)}>{indicator}</span>
      )}
    </button>
  );
}

function SummaryTab({
  trade,
  isLong,
  isOpen,
  sideColor,
  pnlColor,
  otherShots,
  onZoom,
  onGoTab,
}: {
  trade: Trade;
  isLong: boolean;
  isOpen: boolean;
  sideColor: string;
  pnlColor: string;
  otherShots: Trade['screenshots'];
  onZoom: (url: string) => void;
  onGoTab: (tab: TabId) => void;
}): ReactElement {
  const journal = trade.journal;
  const preComplete = journal?.preComplete ?? false;
  const postComplete = journal?.postComplete ?? false;
  const showPostStatus = !isOpen;

  return (
    <div className="space-y-5">
      {/* Stats grid */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Stat label="Side" value={isLong ? '↑ Long' : '↓ Short'} valueClass={sideColor} />
        <Stat label="Cantidad" value={fmt(trade.quantity)} mono />
        <Stat
          label="P&L"
          value={trade.pnl != null ? fmt(trade.pnl, { signDisplay: 'always' }) : '—'}
          valueClass={pnlColor}
          mono
        />
        <Stat
          label="Resultado"
          value={
            trade.outcome
              ? OUTCOME_LABEL[trade.outcome]?.label ?? trade.outcome
              : isOpen
                ? '⏳ abierto'
                : trade.status === 'cancelled'
                  ? 'cancelado'
                  : '✓ cerrado'
          }
        />
      </div>

      {/* Estado del journal — CTAs claras */}
      <div className="rounded-lg border border-surface-border bg-surface-panel/30 p-4">
        <div className="text-[11px] font-semibold text-ink-muted uppercase tracking-wider mb-3">
          Estado del journal
        </div>
        <div className="grid grid-cols-2 gap-3">
          <button
            type="button"
            onClick={() => onGoTab('pre')}
            className={cn(
              'rounded-lg border p-3 text-left transition-colors',
              preComplete
                ? 'border-win-DEFAULT/30 bg-win-DEFAULT/5 hover:bg-win-DEFAULT/10'
                : 'border-warn-DEFAULT/40 bg-warn-DEFAULT/5 hover:bg-warn-DEFAULT/10',
            )}
          >
            <div className="text-[10px] uppercase tracking-wider font-semibold mb-1 flex items-center gap-1">
              <span className={preComplete ? 'text-win-DEFAULT' : 'text-warn-DEFAULT'}>
                {preComplete ? '✓' : '⚠'}
              </span>
              <span className="text-ink-muted">Pre-análisis</span>
            </div>
            <div className="text-xs text-ink-muted">
              {preComplete ? 'Completo' : 'Falta análisis o screenshot pre/entry'}
            </div>
          </button>
          <button
            type="button"
            onClick={() => onGoTab('post')}
            disabled={isOpen}
            className={cn(
              'rounded-lg border p-3 text-left transition-colors',
              isOpen
                ? 'border-surface-border bg-surface-panel/30 opacity-60 cursor-not-allowed'
                : postComplete
                  ? 'border-win-DEFAULT/30 bg-win-DEFAULT/5 hover:bg-win-DEFAULT/10'
                  : 'border-warn-DEFAULT/40 bg-warn-DEFAULT/5 hover:bg-warn-DEFAULT/10',
            )}
          >
            <div className="text-[10px] uppercase tracking-wider font-semibold mb-1 flex items-center gap-1">
              <span
                className={
                  isOpen
                    ? 'text-ink-faint'
                    : postComplete
                      ? 'text-win-DEFAULT'
                      : 'text-warn-DEFAULT'
                }
              >
                {isOpen ? '—' : postComplete ? '✓' : '⚠'}
              </span>
              <span className="text-ink-muted">Post-análisis</span>
            </div>
            <div className="text-xs text-ink-muted">
              {isOpen
                ? 'Disponible al cerrar'
                : showPostStatus && postComplete
                  ? 'Completo'
                  : 'Falta resultado, conclusión o screenshot post'}
            </div>
          </button>
        </div>
      </div>

      {/* Prices */}
      <div className="rounded-lg border border-surface-border bg-surface-panel/30 p-4">
        <div className="text-[11px] font-semibold text-ink-muted uppercase tracking-wider mb-3">
          Precios
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-4 gap-y-3 text-sm">
          <Field label="Entrada">{fmt(trade.entryPrice)}</Field>
          <Field label="Stop Loss">{trade.stopLoss != null ? fmt(trade.stopLoss) : '—'}</Field>
          <Field label="Take Profit">{trade.takeProfit != null ? fmt(trade.takeProfit) : '—'}</Field>
          <Field label="Salida">{trade.exitPrice != null ? fmt(trade.exitPrice) : '—'}</Field>
          <Field label="Hora entrada" wide>
            {fmtDateTime(trade.entryAt)}
          </Field>
          {trade.exitAt && (
            <Field label="Hora salida" wide>
              {fmtDateTime(trade.exitAt)}
            </Field>
          )}
          {trade.fees > 0 && <Field label="Fees">{fmt(trade.fees)}</Field>}
        </div>
      </div>

      {/* Screenshots sin phase asignada (legacy) */}
      {otherShots.length > 0 && (
        <Section title={`Screenshots sin clasificar · ${otherShots.length}`}>
          <ShotGrid shots={otherShots} onZoom={onZoom} />
          <p className="text-[10px] text-ink-faint mt-2">
            Asigná una phase (pre/entry/post) editando el trade para que aparezcan en
            la pestaña correspondiente.
          </p>
        </Section>
      )}
    </div>
  );
}

function PrePostTab({
  kind,
  text,
  shots,
  complete,
  outcome,
  isOpen,
  onEdit,
  onZoom,
}: {
  kind: 'pre' | 'post';
  text: string | null | undefined;
  shots: Trade['screenshots'];
  complete: boolean;
  outcome?: Trade['outcome'];
  isOpen?: boolean;
  onEdit: () => void;
  onZoom: (url: string) => void;
}): ReactElement {
  const isPre = kind === 'pre';
  const empty = !complete;

  if (kind === 'post' && isOpen) {
    return (
      <div className="rounded-lg border border-dashed border-surface-border bg-surface-panel/30 p-8 text-center">
        <div className="text-2xl mb-2">⏳</div>
        <p className="text-sm text-ink-muted">
          El trade está abierto. Cuando cierre vas a poder registrar el post-análisis.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {/* Estado + CTA */}
      <div
        className={cn(
          'rounded-lg border p-3 flex items-center justify-between gap-3',
          complete
            ? 'border-win-DEFAULT/30 bg-win-DEFAULT/5'
            : 'border-warn-DEFAULT/40 bg-warn-DEFAULT/5',
        )}
      >
        <div className="text-xs">
          <span className={complete ? 'text-win-DEFAULT font-semibold' : 'text-warn-DEFAULT font-semibold'}>
            {complete ? '✓ Registrado' : '⚠ Pendiente'}
          </span>
          <span className="text-ink-muted ml-2">
            {complete
              ? isPre
                ? 'Tenés análisis o screenshot pre/entry.'
                : 'Tenés resultado, conclusión o screenshot post.'
              : isPre
                ? 'Sumá tu análisis o un screenshot del setup pre-trade.'
                : 'Sumá el resultado (TP/SL/Manual), tu conclusión, o un screenshot post.'}
          </span>
        </div>
        <Button size="sm" variant={complete ? 'ghost' : 'primary'} onClick={onEdit}>
          {complete ? '✎ Editar' : '+ Agregar'}
        </Button>
      </div>

      {/* Outcome (solo en post tab) */}
      {!isPre && outcome && (
        <Section title="Resultado">
          <span
            className={cn(
              'inline-block text-sm font-semibold px-3 py-1 rounded-md border',
              OUTCOME_LABEL[outcome]?.color ?? 'border-surface-border text-ink',
            )}
          >
            {OUTCOME_LABEL[outcome]?.label ?? outcome}
          </span>
        </Section>
      )}

      {/* Texto */}
      {text ? (
        <Section title={isPre ? 'Análisis pre-trade' : 'Conclusión post-trade'}>
          <p className="text-sm text-ink whitespace-pre-wrap leading-relaxed bg-surface-panel/30 border border-surface-border rounded-lg p-4">
            {text}
          </p>
        </Section>
      ) : empty ? null : null}

      {/* Screenshots de la fase */}
      {shots.length > 0 ? (
        <Section title={`Screenshots · ${shots.length}`}>
          <ShotGrid shots={shots} onZoom={onZoom} />
        </Section>
      ) : (
        <div className="rounded-lg border border-dashed border-surface-border bg-surface-panel/20 p-6 text-center text-xs text-ink-subtle">
          {isPre
            ? 'Sin screenshots pre/entry. Agregá una desde Editar.'
            : 'Sin screenshots post. Agregá una desde Editar.'}
        </div>
      )}
    </div>
  );
}

function ShotGrid({
  shots,
  onZoom,
}: {
  shots: Trade['screenshots'];
  onZoom: (url: string) => void;
}): ReactElement {
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
      {shots.map((s) => (
        <button
          key={s.id}
          type="button"
          onClick={() => onZoom(s.url)}
          className="rounded-lg border border-surface-border bg-surface-elevated overflow-hidden hover:border-surface-strong transition-colors text-left group"
        >
          <div className="aspect-video bg-black overflow-hidden">
            <img
              src={s.url}
              alt=""
              className="w-full h-full object-contain group-hover:scale-105 transition-transform"
              loading="lazy"
            />
          </div>
          <div className="px-2 py-1.5 flex items-center gap-1.5 flex-wrap">
            {s.timeframe && (
              <span className="text-[10px] font-mono font-semibold px-1.5 py-0.5 rounded bg-brand-500/10 text-brand-500 border border-brand-500/30">
                {s.timeframe.toUpperCase()}
              </span>
            )}
            {s.phase && (
              <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-info-DEFAULT/10 text-info-DEFAULT border border-info-DEFAULT/30 capitalize">
                {s.phase}
              </span>
            )}
          </div>
        </button>
      ))}
    </div>
  );
}

function Stat({
  label,
  value,
  valueClass = 'text-ink',
  mono,
}: {
  label: string;
  value: string;
  valueClass?: string;
  mono?: boolean;
}): ReactElement {
  return (
    <div className="rounded-lg border border-surface-border bg-surface-elevated p-3">
      <div className="text-[10px] uppercase tracking-[0.15em] text-ink-subtle font-semibold">
        {label}
      </div>
      <div className={`mt-1 text-lg font-bold ${valueClass} ${mono ? 'font-mono' : ''}`}>
        {value}
      </div>
    </div>
  );
}

function Section({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}): ReactElement {
  return (
    <div>
      <div className="text-[11px] font-semibold text-ink-muted uppercase tracking-wider mb-2">
        {title}
      </div>
      {children}
    </div>
  );
}

function Field({
  label,
  children,
  wide,
}: {
  label: string;
  children: React.ReactNode;
  wide?: boolean;
}): ReactElement {
  return (
    <div className={wide ? 'col-span-2' : ''}>
      <div className="text-[10px] uppercase tracking-wider text-ink-subtle">{label}</div>
      <div className="font-mono text-ink mt-0.5">{children}</div>
    </div>
  );
}
